import { useActiveOrganizationListener } from 'hooks/useActiveOrganizationListener';
import {
  useEditableCard,
  CardGridLayout,
  GridInput
} from 'lib/components/Card/Grid';
import { ColumnSelect } from 'lib/components/ColumnSelect';
import { Form } from 'lib/components/Form';
import { TextField } from 'lib/components/TextField';
import { TimezoneType, State } from 'lib/enums';
import { removeUndefinedFields } from 'lib/helpers';
import { MAX_ORGANIZATION_NAME_LENGTH } from 'lib/types/organization';
import { useAppDispatch } from 'redux/hooks';
import ToastActions from 'redux/toast';

export default function PublisherInfo() {
  const dispatch = useAppDispatch();
  const activeOrganization = useActiveOrganizationListener();

  const {
    name,
    iana_timezone,
    state,
    address,
    addressLine2,
    county,
    city,
    zipCode,
    phone,
    email,
    publisherPointOfContact,
    website
  } = activeOrganization?.data() ?? {};

  const { formSettings, onChange, disabled, editable, onSubmitWrapper } =
    useEditableCard({
      name,
      iana_timezone,
      state,
      address,
      addressLine2,
      county,
      city,
      zipCode,
      phone,
      email,
      publisherPointOfContact,
      website
    });
  return (
    <Form
      id="publisher-settings-info"
      onSubmit={() =>
        onSubmitWrapper(async () => {
          await activeOrganization?.ref.update(
            removeUndefinedFields(formSettings)
          );
          dispatch(
            ToastActions.toastSuccess({
              headerText: 'Success',
              bodyText: 'Publisher settings updated successfully.'
            })
          );
          return { success: true };
        })
      }
    >
      <CardGridLayout
        header={{ title: 'General Information' }}
        editable={editable}
      >
        <GridInput>
          <TextField
            labelText="Name of publisher"
            required
            onChange={name => onChange({ name })}
            value={formSettings.name}
            disabled={disabled}
            maxLength={MAX_ORGANIZATION_NAME_LENGTH}
            id="name"
          />
        </GridInput>
        <GridInput>
          <ColumnSelect
            labelText="Timezone"
            size="md"
            options={TimezoneType.items().map(tz => ({
              value: tz.key,
              label: tz.label
            }))}
            onChange={iana_timezone => onChange({ iana_timezone })}
            value={formSettings.iana_timezone || ''}
            disabled={disabled}
            id="timezone"
          />
        </GridInput>
        <GridInput>
          <ColumnSelect
            labelText="State"
            options={State.items().map(state => ({
              value: state.value.toString(),
              label: state.label
            }))}
            onChange={stateStr => onChange({ state: Number(stateStr) })}
            value={formSettings.state?.toString() || ''}
            disabled={disabled}
            id="state"
          />
        </GridInput>
        <GridInput>
          <TextField
            labelText="Address line 1"
            required
            onChange={address => onChange({ address })}
            value={formSettings.address}
            disabled={disabled}
            id="address"
          />
        </GridInput>
        <GridInput>
          <TextField
            labelText="Address line 2"
            onChange={addressLine2 => onChange({ addressLine2 })}
            value={formSettings.addressLine2}
            disabled={disabled}
            id="address-2"
          />
        </GridInput>
        <GridInput>
          <TextField
            labelText="County"
            required
            onChange={county => onChange({ county })}
            value={formSettings.county}
            disabled={disabled}
            id="county"
          />
        </GridInput>
        <GridInput>
          <TextField
            labelText="City"
            required
            onChange={city => onChange({ city })}
            value={formSettings.city}
            disabled={disabled}
            id="city"
          />
        </GridInput>
        <GridInput>
          <TextField
            labelText="Zip Code"
            required
            onChange={zipCode => onChange({ zipCode })}
            value={formSettings.zipCode}
            disabled={disabled}
            type="postal-code"
            id="zip"
          />
        </GridInput>
        <GridInput>
          <TextField
            labelText="Phone number"
            required
            onChange={phone => onChange({ phone })}
            value={formSettings.phone}
            disabled={disabled}
            type="tel"
            id="phone"
          />
        </GridInput>
        <GridInput>
          <TextField
            labelText="Publisher email"
            onChange={email => onChange({ email })}
            value={formSettings.email}
            disabled={disabled}
            type="email"
            id="email"
          />
        </GridInput>
        <GridInput>
          <TextField
            labelText="Publisher point of contact name"
            onChange={publisherPointOfContact =>
              onChange({ publisherPointOfContact })
            }
            value={formSettings.publisherPointOfContact}
            disabled={disabled}
            id="primary-contact-name"
          />
        </GridInput>
        <GridInput>
          <TextField
            labelText="Publisher website"
            onChange={website => onChange({ website })}
            value={formSettings.website}
            disabled={disabled}
            type="url"
            id="website"
          />
        </GridInput>
      </CardGridLayout>
    </Form>
  );
}
